import { adminApi, adminApiInZone } from './api-client';
import { ZONES, fanOutZones, failedZoneLabels, throwIfAllZonesFailed, type PortalZone, HOME_ZONE } from './zones';

/**
 * Typed wrappers around the admin-api Nexa Config + Monitoring routes
 * (Phase 5d-5 / 5c, ADR-046 §5 + §6 — lambdas/admin-api/src/handlers.ts). The
 * Lambda is the authority for the response shapes; this module mirrors them.
 *
 * Read routes (listVendors / getVendorCandles) gate on the mutating-tier-read
 * set (super_admin / nexa_admin / support). `getPlatformConfig` is the one
 * EXCEPTION: it is pinned to the PUT's own {super_admin, nexa_admin} set, so
 * adding visibility of the values did not also widen who may see them. Write
 * routes split:
 *   - updatePlatformParam → MUTATING_ROLES (super_admin + nexa_admin), fresh-auth
 *   - updateVendorRegistry / setVendorEnabled → super_admin only, fresh-auth
 * The portal gates the edit controls to super_admin to match; every mutation is
 * also gated server-side, so the surface never relies on the client gate alone.
 */

/**
 * Route one call to a SPECIFIC zone (ADR-122 Patterns 3 and 4).
 *
 * ⚠ OMITTING THE ZONE MEANS "HOME", NOT "ALL" — identical in shape and intent to
 * `customers-api.ts`'s own `call`. Every function below takes an optional trailing `zone`, so
 * a caller that has not been migrated behaves exactly as it did before ADR-122 rather than
 * changing target silently.
 *
 * This module is consumed by TWO pages under two different patterns, and they must not be
 * confused:
 *   - `NexaConfigPage` (Pattern 3) passes the SELECTED zone — a config write targets exactly
 *     one jurisdiction, and a merged view of two mutation targets is meaningless.
 *   - `MonitoringPage` (Pattern 4) calls the vendor reads once PER zone and sums them, because
 *     a vendor-spend figure missing a zone is WRONG, not merely short.
 */
const call = <T>(zone: PortalZone | undefined, path: string, init?: RequestInit): Promise<T> =>
  zone ? adminApiInZone<T>(zone, path, init) : adminApi<T>(path, init);

// ---------------------------------------------------------------------------
// Platform parameters (Nexa Config page — ADR-046 §5)
// ---------------------------------------------------------------------------

/** A tunable platform parameter. `min`/`max` are the documented bounds the
 *  server enforces; surfaced as the "range" line on the value card. */
export interface PlatformParam {
  name: string;
  value: number;
  min: number;
  max: number;
  /** The value the report pipeline falls back to when the param is unseeded. */
  default: number;
  /**
   * ⭐ `'default'` MEANS NOBODY HAS EVER SET THIS — it is not a read failure, and it is not a
   * stored value either. The consumers (`report-scheduler`, `report-generate`,
   * and, until 5 October 2026, `enrolment-reaper`) each fall back to a documented default when the item is absent, so the
   * pipeline is using `default` — but rendering it as though it had been read is the defect
   * this whole route exists to remove, one layer down. The card says which it is.
   */
  source: 'persisted' | 'default';
  updatedAt?: string | null;
  updatedBy?: string | null;
  updatedReason?: string | null;
}

/**
 * GET /admin/config/platform — the PERSISTED parameters for one zone, with audit metadata.
 *
 * ⭐⭐ BEFORE THIS CALL THE PARAMETERS TAB WAS WRITE-ONLY. It rendered `reportWindowDays` and
 * `minimumFullMonths` from a hardcoded client constant, so the card showed `5` and `1` whether
 * or not those were the stored values and whether or not a previous edit had landed — and with
 * two zones holding independent values, the same pair was shown for both. The page could not
 * perform the one check Pattern 3 exists to make possible: edit in one zone, switch, see the
 * other unchanged.
 *
 * ⚠ ZONE IS NOT OPTIONAL IN PRACTICE HERE even though the signature allows it, for the same
 * reason every call in this module takes one: omitting it means HOME, and a Parameters tab that
 * read home while the selector said United States would be the original defect with a new label.
 * `NexaConfigPage` always passes the selected zone.
 */
export function getPlatformConfig(
  /** The zone to read. Omitted = the home zone. */
  zone?: PortalZone,
): Promise<{ params: PlatformParam[] }> {
  return call<{ params: PlatformParam[] }>(zone, '/admin/config/platform');
}

/**
 * PUT /admin/config/platform/{param} — tune reportWindowDays / minimumFullMonths
 * without a redeploy. `reason` is an optional audit note (the Nexa Config edit
 * modal makes it required at the UI layer). Returns the persisted value + audit
 * metadata.
 */
export function updatePlatformParam(
  param: string,
  value: number,
  reason: string,
  /** The zone this edit targets. Omitted = the home zone. */
  zone?: PortalZone,
): Promise<{ param: string; value: number; updatedAt: string; updatedBy: string }> {
  return call(zone, `/admin/config/platform/${encodeURIComponent(param)}`, {
    method: 'PUT',
    body: JSON.stringify(reason ? { value, reason } : { value }),
  });
}

// ---------------------------------------------------------------------------
// SOW availability gate (Nexa Config → Availability Gate — ADR-080 Amendment 1)
// ---------------------------------------------------------------------------

/**
 * ⭐⭐ EACH ZONE HOLDS ITS OWN TWO GATES, SO EVERY GATE CALL TAKES A ZONE (ADR-124 D6 / I8,
 * 30 September 2026).
 *
 * The launch order is "US open, Australia closed" — one platform-wide switch cannot express it.
 * Each zone's `platform-ops` serves the gate routes against its own `Nexa-AgentConfig` row, so the
 * gates are Pattern 3 exactly like the Parameters tab: `NexaConfigPage` passes the SELECTED zone.
 *
 * ⚠ THE ZONE IS REQUIRED HERE, NOT OPTIONAL AS IN `call`. For a parameter, "omitted means home"
 * preserves an unmigrated caller; for a kill-switch it is a silent mis-target — the UAT test 11
 * failure (23 September 2026) in reverse. So the gate functions cannot be called without naming
 * the zone they read or change. `zone-routing.test.ts` pins every gate call to the zone it is
 * handed.
 *
 * ADR-124 I8 (30 September 2026) — each zone's toggle now writes ITS OWN key at the sow-gate
 * Worker (`sow:gate:<zone>`), which enforces it at the edge; no zone is special. The PLATFORM
 * KILL-SWITCH (the global key, which closes every zone at once) is a separate control below.
 */
const gateCall = <T>(zone: PortalZone, path: string, init?: RequestInit): Promise<T> =>
  adminApiInZone<T>(zone, path, init);

/** The authoritative gate state (mirrors the sow-gate Worker's KV value). */
export interface SowGateState {
  mode: 'open' | 'closed';
  /** Who closed it — 'agent' shows the human-only-reopen banner (D5). */
  closedBy?: 'human' | 'agent' | null;
  reason?: string | null;
  closedAt?: string | null;
  updatedAt?: string | null;
  updatedBy?: string | null;
  /** true when the read fell back to the mirror (the Worker was unreachable). */
  degraded?: boolean;
  /**
   * `worker` — read from the edge, which enforces it. `unset` — the Worker was unreachable and
   * the zone's mirror row has never been set: CLOSED by default. `unavailable` — the read failed;
   * reported closed, never open.
   */
  source?: 'worker' | 'mirror' | 'zone' | 'unset' | 'unavailable' | string;
}

/** GET /admin/config/sow-gate — ONE zone's public sign-up gate state for display. */
export function getSowGate(zone: PortalZone): Promise<SowGateState> {
  return gateCall(zone, '/admin/config/sow-gate', { method: 'GET' });
}

/**
 * PUT /admin/config/sow-gate — human toggle of ONE zone's public sign-up gate.
 * `reason` is an audit note (required at the UI layer). Agent-closed gates are
 * reopened here (a human open is always permitted; the agent can only close — D5).
 * `worker` is present only for the home zone, whose toggle also drives the edge key.
 */
export function setSowGate(
  mode: 'open' | 'closed',
  reason: string,
  /** The zone this toggle changes — and the only one. */
  zone: PortalZone,
): Promise<{ mode: 'open' | 'closed'; updatedAt: string; updatedBy: string; worker?: SowGateState }> {
  return gateCall(zone, '/admin/config/sow-gate', {
    method: 'PUT',
    body: JSON.stringify(reason ? { mode, reason } : { mode }),
  });
}

// ---------------------------------------------------------------------------
// Beta-invites gate (Nexa Config → Availability Gate — Punchlist #9 A)
//
// The SECOND, INDEPENDENT availability gate: the open/closed switch on partner
// beta onboarding. Same state shape and same tiers as the SOW gate; entirely
// separate state. Closing public sign-ups does not stop beta onboarding, and
// closing beta invites does not stop public sign-ups.
// ---------------------------------------------------------------------------

/** GET /admin/config/beta-gate — ONE zone's beta-invites gate state for display. */
export function getBetaGate(zone: PortalZone): Promise<SowGateState> {
  return gateCall(zone, '/admin/config/beta-gate', { method: 'GET' });
}

/**
 * PUT /admin/config/beta-gate — human toggle of ONE zone's beta-invites gate. While
 * closed, partner operators cannot start a BOW: the Start BOW control is hidden
 * and `POST /portal/bow/launch` refuses. The participants list stays visible —
 * closing the gate stops NEW onboarding, not visibility.
 */
export function setBetaGate(
  mode: 'open' | 'closed',
  reason: string,
  /** The zone this toggle changes — and the only one. */
  zone: PortalZone,
): Promise<{ mode: 'open' | 'closed'; updatedAt: string; updatedBy: string; worker?: SowGateState }> {
  return gateCall(zone, '/admin/config/beta-gate', {
    method: 'PUT',
    body: JSON.stringify(reason ? { mode, reason } : { mode }),
  });
}

// ---------------------------------------------------------------------------
// ADR-124 I8 — the PLATFORM KILL-SWITCH (one per gate)
//
// The global key the Worker ANDs with every zone's own key: closed, it shuts every zone at once,
// whatever each zone's switch says. It is one key, so any zone's API writes the same thing; the
// home zone is used because it always exists. Same routes as the zone toggles, `scope: platform`.
// ---------------------------------------------------------------------------

/** GET the kill-switch for `gate` ('sow' = public sign-ups, 'beta' = beta invites). */
export function getKillSwitch(gate: 'sow' | 'beta'): Promise<SowGateState> {
  return gateCall(HOME_ZONE, `/admin/config/${gate}-gate?scope=platform`, { method: 'GET' });
}

/** PUT the kill-switch — `closed` shuts `gate` in EVERY zone. */
export function setKillSwitch(
  gate: 'sow' | 'beta',
  mode: 'open' | 'closed',
  reason: string,
): Promise<{ mode: 'open' | 'closed'; updatedAt: string; updatedBy: string }> {
  return gateCall(HOME_ZONE, `/admin/config/${gate}-gate`, {
    method: 'PUT',
    body: JSON.stringify(reason ? { mode, reason, scope: 'platform' } : { mode, scope: 'platform' }),
  });
}

// ---------------------------------------------------------------------------
// Vendor registry + usage (Monitoring page — ADR-046 §6)
// ---------------------------------------------------------------------------

/** One daily VendorUsage aggregate row (the 30-day history). */
export interface VendorUsageDay {
  day: string;
  calls: number;
  units: number;
  errors: number;
}

/**
 * A vendor registry record enriched with its trailing 30-day usage. Mirrors
 * listVendors (ADR-046 §6.1). `domain` powers the favicon; `warnPerMin` /
 * `dangerPerMin` are the per-vendor candle thresholds. Several fields are
 * optional because the registry rows seeded at 5b carry a subset.
 */
export interface Vendor {
  key: string;
  displayName: string;
  category: string;
  pricingModel: string;
  balanceMode: string;
  minimumFloat: number;
  status: string;
  healthCheck: string;
  accountEmail: string | null;
  updatedAt: string | null;
  /** Audit metadata from the last status/registry write (ADR-046 §6). Surfaced
   *  on the candle panel as the "Set DOWN by {name} · {timestamp}" actor line
   *  when a vendor is DOWN (batch-2 Stage 1). `updatedByName` is the resolved
   *  staff display name (falls back to the staff id / sub server-side). */
  updatedBy?: string | null;
  updatedByName?: string | null;
  updatedReason?: string | null;
  usage30d: VendorUsageDay[];
  // Fields the registry may carry (ADR-046 §6.1) — optional until seeded.
  domain?: string | null;
  warnPerMin?: number | null;
  dangerPerMin?: number | null;
  enabled?: boolean | null;
  secretName?: string | null;
  balanceValue?: number | string | null;
}

export function listVendors(
  /** The zone to read. Omitted = the home zone. */
  zone?: PortalZone,
): Promise<{ vendors: Vendor[] }> {
  return call<{ vendors: Vendor[] }>(zone, '/admin/config/vendors');
}

/** One 10-minute OHLC bucket of calls-per-minute for the candlestick wall. */
export interface VendorCandle {
  /** Bucket start, Unix epoch seconds (10-min aligned). */
  t: number;
  open: number;
  high: number;
  low: number;
  close: number;
  calls: number;
  errors: number;
}

/**
 * GET /admin/config/vendors/{key}/candles — trailing 24h of 10-min OHLC buckets.
 * Returns `{ buckets: [] }` when the metering emitter has not run yet; callers
 * fall back to a synthetic render (the backend route is stubbed at 5c — real
 * data flows once the 10-min emitter deploys).
 */
export function getVendorCandles(
  vendorKey: string,
  /** The zone to read. Omitted = the home zone. */
  zone?: PortalZone,
): Promise<{ buckets: VendorCandle[] }> {
  return call<{ buckets: VendorCandle[] }>(zone,
    `/admin/config/vendors/${encodeURIComponent(vendorKey)}/candles`,
  );
}

/** Fields editable from the Edit-registry panel (super_admin). */
export interface VendorRegistryPatch {
  warnPerMin?: number;
  dangerPerMin?: number;
  status?: 'active' | 'suspended' | 'not_contracted';
  accountEmail?: string;
  minimumFloat?: number;
}

/**
 * PUT /admin/config/vendors/{key} — edit the vendor registry record. `reason`
 * is the required audit note. super_admin only (server-gated).
 */
export function updateVendorRegistry(
  key: string,
  patch: VendorRegistryPatch,
  reason: string,
  /** The zone this edit targets. Omitted = the home zone. */
  zone?: PortalZone,
): Promise<{ key: string; updatedAt: string; updatedBy: string }> {
  return call(zone, `/admin/config/vendors/${encodeURIComponent(key)}`, {
    method: 'PUT',
    body: JSON.stringify({ ...patch, reason }),
  });
}

/**
 * PUT /admin/config/vendors/{key}/enabled — manual disable/enable. A disabled
 * vendor is skipped by the report pipeline (dependent assessment degrades to
 * `partial`). `reason` required. super_admin only (server-gated).
 */
export function setVendorEnabled(
  key: string,
  enabled: boolean,
  reason: string,
  /** The zone this toggle targets. Omitted = the home zone. */
  zone?: PortalZone,
): Promise<{ key: string; enabled: boolean; updatedAt: string; updatedBy: string }> {
  return call(zone, `/admin/config/vendors/${encodeURIComponent(key)}/enabled`, {
    method: 'PUT',
    body: JSON.stringify({ enabled, reason }),
  });
}

/**
 * PUT /admin/config/vendors/{key}/status — outage-SIMULATE toggle (batch Stage 3).
 * Flips the registry `status` to `down`/`active` so UAT can raise/clear a real
 * `vendor:api_down` critical via the alert-evaluator without the CLI. DISTINCT
 * from setVendorEnabled (the pipeline disable toggle): `down` raises the alert
 * but does not stop real vendor calls. super_admin only (server-gated). Manual
 * revert only.
 */
export function setVendorStatus(
  key: string,
  status: 'down' | 'active',
  reason: string,
  /** The zone this toggle targets. Omitted = the home zone. */
  zone?: PortalZone,
): Promise<{ key: string; status: string; updatedAt: string; updatedBy: string }> {
  return call(zone, `/admin/config/vendors/${encodeURIComponent(key)}/status`, {
    method: 'PUT',
    body: JSON.stringify({ status, reason }),
  });
}

// ---------------------------------------------------------------------------
// ADR-122 Pattern 4 — fan out WITH AN AGGREGATE TOTAL (Monitoring page)
//
// ⭐⭐ WHY MONITORING IS ITS OWN PATTERN RATHER THAN PATTERN 1. On Customers or
// Nexa Interest, a missing zone means missing ROWS: the list is short, and the
// warning says so. Here a missing zone means the NUMBERS ARE WRONG. "We made
// 41,000 vendor calls this month" with one jurisdiction absent is not an
// incomplete answer, it is a false one — and unlike a short list, nothing about
// it looks wrong. So this page sums, shows the per-zone breakdown that produced
// the sum, and marks the total as incomplete the moment a zone drops out.
// ---------------------------------------------------------------------------

/** One zone's contribution to a merged vendor row — the breakdown behind the total. */
export interface VendorZoneUsage {
  zoneCode: string;
  zoneLabel: string;
  calls: number;
  units: number;
  errors: number;
}

export interface MergedVendor extends Vendor {
  /** Per-zone 30-day figures, in `ZONES` order. Present even when only one zone answered. */
  byZone: VendorZoneUsage[];
}

/** One zone's 30-day total across all its vendors — a row of the Vendor usage table. */
export interface ZoneVendorTotal extends VendorZoneUsage {
  /** How many vendors this zone's registry lists. 0 means its usage could not be read at all. */
  registered: number;
}

export interface VendorsFanout {
  vendors: MergedVendor[];
  /** 30-day totals across every zone that ANSWERED — exactly the sum of `zoneTotals`. */
  totals: { calls: number; units: number; errors: number };
  /**
   * One row per zone that ANSWERED, in `ZONES` order — including a zone with no usage, as 0
   * (quality register Q16, 31 October 2026). Built per zone, not from the merged vendor rows: a
   * zone that contributes no vendor row used to vanish from the table while the headline still
   * said "calls across all zones".
   */
  zoneTotals: ZoneVendorTotal[];
  /** Labels of zones that did not answer — the reason `totals` may be short. */
  failedZones: string[];
}

const sumUsage = (days: VendorUsageDay[]) =>
  days.reduce(
    (a, d) => ({ calls: a.calls + d.calls, units: a.units + d.units, errors: a.errors + d.errors }),
    { calls: 0, units: 0, errors: 0 },
  );

/**
 * Every vendor, merged BY KEY across zones, with per-zone usage retained.
 *
 * ⚠ MERGED BY KEY, NOT CONCATENATED. The vendor registry is platform configuration seeded
 * identically into each zone's stack, so the same `serper` appears in both — listing it twice
 * would read as two vendors with half the traffic each. What differs per zone is the USAGE,
 * which is why the daily series are summed day-by-day and the per-zone split is kept beside
 * the merged row rather than thrown away.
 *
 * The registry fields (thresholds, status, account email) are taken from the HOME zone's copy:
 * they are the same config in every zone, and picking one deterministically beats blending two
 * copies of the same value.
 */
export async function listAllVendors(): Promise<VendorsFanout> {
  const results = await fanOutZones(async (zone) => {
    const { vendors } = await listVendors(zone);
    return [{ zone, vendors }];
  });
  // No zone answered → a failed read, not a platform with zero vendor traffic.
  throwIfAllZonesFailed(results);

  const answered = results.flatMap((r) => r.items);
  const byKey = new Map<string, MergedVendor>();

  for (const { zone, vendors } of answered) {
    for (const v of vendors) {
      const existing = byKey.get(v.key);
      const usage = sumUsage(v.usage30d ?? []);
      const contribution: VendorZoneUsage = {
        zoneCode: zone.code, zoneLabel: zone.label, ...usage,
      };

      if (!existing) {
        byKey.set(v.key, { ...v, byZone: [contribution] });
        continue;
      }
      // Registry fields already came from whichever zone was seen first; ZONES puts home
      // first, so that is the home copy. Only the usage series accumulates.
      existing.usage30d = mergeUsageSeries(existing.usage30d ?? [], v.usage30d ?? []);
      existing.byZone.push(contribution);
    }
  }

  const vendors = [...byKey.values()];

  // ⭐ Q16 — one row per answering zone, summed from THAT zone's own vendors, so a zone with no
  // usage reads 0 instead of disappearing, and the headline is computed as the sum of exactly
  // these rows. A zone whose registry lists no vendors is flagged (`registered: 0`): its usage
  // cannot be read through the registry at all, which is what hid us-east-1's calls until
  // 31 October 2026.
  const zoneTotals: ZoneVendorTotal[] = answered.map(({ zone, vendors: zv }) => ({
    zoneCode: zone.code,
    zoneLabel: zone.label,
    registered: zv.length,
    ...zv.reduce(
      (a, v) => {
        const t = sumUsage(v.usage30d ?? []);
        return { calls: a.calls + t.calls, units: a.units + t.units, errors: a.errors + t.errors };
      },
      { calls: 0, units: 0, errors: 0 },
    ),
  }));
  const totals = zoneTotals.reduce(
    (a, z) => ({ calls: a.calls + z.calls, units: a.units + z.units, errors: a.errors + z.errors }),
    { calls: 0, units: 0, errors: 0 },
  );

  return { vendors, totals, zoneTotals, failedZones: failedZoneLabels(results) };
}

/**
 * Add two 30-day series together, matched on `day`.
 *
 * ⚠ MATCHED ON THE DAY KEY, NOT ZIPPED BY INDEX. The two zones' series can differ in length
 * and start date — a zone that has existed for a week has a shorter history than one that has
 * run for a year — so index-wise addition would silently add last Tuesday's US calls to last
 * March's AU calls. A day present in only one zone survives with that zone's figures.
 */
function mergeUsageSeries(a: VendorUsageDay[], b: VendorUsageDay[]): VendorUsageDay[] {
  const byDay = new Map<string, VendorUsageDay>();
  for (const d of [...a, ...b]) {
    const prev = byDay.get(d.day);
    byDay.set(d.day, prev
      ? { day: d.day, calls: prev.calls + d.calls, units: prev.units + d.units, errors: prev.errors + d.errors }
      : { ...d });
  }
  return [...byDay.values()].sort((x, y) => x.day.localeCompare(y.day));
}

/**
 * One vendor's candle wall, summed across zones.
 *
 * Buckets are matched on their epoch-second `t` and added: each field is a calls-per-minute
 * figure for that slice, and the platform-wide rate in a slice IS the sum of the zones' rates
 * in it. A zone that fails contributes nothing rather than rejecting the wall — a candle wall
 * missing one jurisdiction is still a truer picture than no wall, and the page's zone warning
 * is what says the figures are short.
 */
export async function getAllVendorCandles(vendorKey: string): Promise<{ buckets: VendorCandle[] }> {
  const settled = await Promise.allSettled(ZONES.map((z) => getVendorCandles(vendorKey, z)));
  const byT = new Map<number, VendorCandle>();
  for (const r of settled) {
    if (r.status !== 'fulfilled') continue;
    for (const c of r.value.buckets) {
      const prev = byT.get(c.t);
      byT.set(c.t, prev
        ? {
            t: c.t,
            open: prev.open + c.open,
            high: prev.high + c.high,
            low: prev.low + c.low,
            close: prev.close + c.close,
            calls: prev.calls + c.calls,
            errors: prev.errors + c.errors,
          }
        : { ...c });
    }
  }
  return { buckets: [...byT.values()].sort((x, y) => x.t - y.t) };
}

/**
 * Apply a vendor-registry write to EVERY zone.
 *
 * ⭐⭐ THIS IS THE ONE FAN-OUT IN ADR-122 THAT WRITES, and it is deliberate rather than an
 * oversight of Pattern 3's "a write targets one zone" rule. The distinction is what the record
 * IS. A customer, a brief, a subscriber belongs to a jurisdiction, so a write to it belongs
 * there too. A vendor registry row is PLATFORM CONFIGURATION, seeded identically into every
 * zone's stack — marking Serper DOWN in AU while US keeps calling it is not a per-zone
 * decision, it is a half-applied one, and the report pipeline in the other zone would go on
 * hammering a vendor an operator believes they have stopped.
 *
 * ⚠ THIS IS NOT A RESIDENCY CONCERN. ADR-120 Amendment 2 forbids one zone's Lambda reading
 * another zone's CUSTOMER data. This is the operator's own browser writing the same config to
 * each zone with one globally-valid staff token — no personal data, and no zone touching
 * another zone's records.
 *
 * ⚠⚠ A PARTIAL WRITE IS REPORTED, NEVER SWALLOWED. If one zone rejects, the caller is handed
 * the zones that failed so the UI can say "applied in Australia, not in United States" —
 * because a config write that half-landed and reported success is how the two zones diverge
 * without anyone knowing they have.
 */
export async function applyVendorWriteToAllZones<T>(
  write: (zone: PortalZone) => Promise<T>,
): Promise<{ results: T[]; failedZones: string[] }> {
  const settled = await Promise.allSettled(ZONES.map(write));
  const failed = settled.flatMap((r, i) => (r.status === 'rejected' ? [ZONES[i]!.label] : []));
  // Every zone rejected → this is a failed write; surface the real error rather than a
  // success-shaped result carrying a list of complaints.
  if (failed.length === ZONES.length) {
    throw (settled[0] as PromiseRejectedResult).reason;
  }
  return {
    results: settled.flatMap((r) => (r.status === 'fulfilled' ? [r.value] : [])),
    failedZones: failed,
  };
}
